import { useRef, useState } from "react";
import { Link, type To } from "react-router";
import type { CustomerBasketArtworkSlot } from "@shared-core/data/customer-basket-types";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Button } from "../../../app/components/primitives/button";
import { validatePdf } from "../PortalArtworkCheckPage";
import { SupplyGuide } from "../../customer-pages/SupplyGuide";
import {
  CheckCircle2,
  Circle,
  Clock,
  Info,
  Loader2,
  XCircle,
  type LucideIcon,
} from "lucide-react";

const QUIETER_TIERS = [
  { tier: "we_will_sort", title: "We'll sort this for you" },
  { tier: "worth_knowing", title: "Worth knowing" },
] as const;

// One slot per version and part of a line, saying whether its file is missing, checked or out of date. A file
// is optional now: the person can add it here, or after ordering from the link we email. Read-only (a build
// that cannot change the basket, or a slot with no URL to change it by) it only says where each file stands.
// "Add your file" runs
// the same file check as the portal's Check artwork page (the page hands the file to `onAddFile`, which checks
// it and puts the check on the slot); the verdict shown is the backend's one-line summary. The icon follows the
// verdict (a tick only for a file that is good to go), and a file we can't print says why, in the check page's
// own words, and what to do; what we will sort for them follows, more quietly.
export function ArtworkSlots({
  slots,
  onAddFile,
  onRemoveFile,
  checkResultTo,
  disabled = false,
  readOnly = false,
}: {
  slots: CustomerBasketArtworkSlot[];
  /** Checks the file and attaches the check to the slot; rejects with the reason it could not. */
  onAddFile: (slot: CustomerBasketArtworkSlot, file: File) => Promise<void>;
  onRemoveFile: (slot: CustomerBasketArtworkSlot) => Promise<void>;
  /** Where the full result of a check is (the Check artwork result page); no link is drawn without it. */
  checkResultTo?: (checkId: string) => To;
  disabled?: boolean;
  /** Draw no button: this build cannot change the basket. */
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Your file</p>
      <ul className="space-y-2">
        {slots.map((slot) => (
          <ArtworkSlotRow
            key={`${slot.version_position}-${slot.part_key}`}
            slot={slot}
            showPartLabel={slots.length > 1}
            onAddFile={onAddFile}
            onRemoveFile={onRemoveFile}
            checkResultTo={checkResultTo}
            disabled={disabled}
            readOnly={readOnly}
          />
        ))}
      </ul>
    </div>
  );
}

function ArtworkSlotRow({
  slot,
  showPartLabel,
  onAddFile,
  onRemoveFile,
  checkResultTo,
  disabled,
  readOnly,
}: {
  slot: CustomerBasketArtworkSlot;
  showPartLabel: boolean;
  onAddFile: (slot: CustomerBasketArtworkSlot, file: File) => Promise<void>;
  onRemoveFile: (slot: CustomerBasketArtworkSlot) => Promise<void>;
  checkResultTo?: (checkId: string) => To;
  disabled: boolean;
  readOnly: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(
        mutationErrorMessage(
          err,
          "We couldn't do that just now. Please try again.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  function choose(file: File | null) {
    if (!file) return;
    const problem = validatePdf(file);
    if (problem) {
      setError(problem);
      return;
    }
    void run(() => onAddFile(slot, file));
  }

  const label = showPartLabel ? slot.part_label : "Artwork";
  const { Icon, iconClass } = slotIcon(slot);
  const cantPrint = slot.state === "checked" && slot.verdict === "cant_print";
  const blocking = slot.findings.filter((f) => f.tier === "cant_print");

  return (
    <li className="rounded-md border bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${iconClass}`} />
          <div className="min-w-0 text-sm">
            <p className="font-medium">{label}</p>
            <p
              className={
                cantPrint
                  ? "font-medium text-red-700 dark:text-red-400"
                  : "text-muted-foreground"
              }
            >
              {slot.state === "none" ? "No file yet" : slot.summary}
            </p>
          </div>
        </div>
        {!readOnly && slot.attach_url !== null && (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="sr-only"
              aria-label={`Choose a PDF for ${label}`}
              onChange={(event) => {
                choose(event.target.files?.[0] ?? null);
                event.target.value = "";
              }}
            />
            {busy ? (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Checking your file…
              </span>
            ) : (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant={slot.state === "none" ? "outline" : "ghost"}
                  disabled={disabled}
                  onClick={() => inputRef.current?.click()}
                >
                  {slot.state === "none"
                    ? "Add your file"
                    : slot.state === "expired"
                      ? "Add your file again"
                      : "Replace"}
                </Button>
                {slot.detach_url !== null && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => void run(() => onRemoveFile(slot))}
                  >
                    Remove
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {!readOnly && slot.attach_url !== null && (
        <SupplyGuide
          className="mt-2"
          sentence={slot.supply_sentence}
          notes={slot.supply_notes}
          terms={slot.supply_terms}
        />
      )}
      {cantPrint && (
        <div className="mt-2 space-y-1.5 text-sm">
          {blocking.length > 0 && (
            <ul className="space-y-1">
              {blocking.map((f) => (
                <li key={f.id}>{f.sentence}</li>
              ))}
            </ul>
          )}
          {slot.next_step && <p className="font-medium">{slot.next_step}</p>}
        </div>
      )}
      {QUIETER_TIERS.map(({ tier, title }) => {
        const shown = slot.findings.filter((f) => f.tier === tier);
        if (shown.length === 0) return null;
        return (
          <div
            key={tier}
            className="mt-2 space-y-1 text-sm text-muted-foreground"
          >
            <p className="font-medium">{title}</p>
            <ul className="space-y-1">
              {shown.map((f) => (
                <li key={f.id}>{f.sentence}</li>
              ))}
            </ul>
          </div>
        );
      })}
      {slot.state === "checked" &&
        slot.check_id !== null &&
        slot.findings.length > 0 &&
        checkResultTo && (
          <Link
            to={checkResultTo(slot.check_id)}
            className="mt-2 inline-block text-sm underline underline-offset-2"
          >
            See what we found
          </Link>
        )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
    </li>
  );
}

// A tick only for a file that is good to go; a warning for one we can't print; a quiet note for what we'll sort or
// what is worth knowing; the clock for a file held for a closer look or a check that has lapsed.
function slotIcon(slot: CustomerBasketArtworkSlot): {
  Icon: LucideIcon;
  iconClass: string;
} {
  if (slot.state === "expired") {
    return { Icon: Clock, iconClass: "text-amber-700 dark:text-amber-400" };
  }
  if (slot.state === "none") {
    return { Icon: Circle, iconClass: "text-muted-foreground" };
  }
  switch (slot.verdict) {
    case "passed":
      return {
        Icon: CheckCircle2,
        iconClass: "text-green-700 dark:text-green-400",
      };
    case "cant_print":
      return { Icon: XCircle, iconClass: "text-red-700 dark:text-red-400" };
    case "we_will_sort":
    case "worth_knowing":
      return { Icon: Info, iconClass: "text-muted-foreground" };
    default:
      return { Icon: Clock, iconClass: "text-muted-foreground" };
  }
}
